import React, { useEffect, useState } from "react";
import { fetchAll, createDoc, updateDocById, deleteDocById, WithId } from "../lib/firestoreData";

interface Kurum {
  ad: string;
  kategori: string;
  avantaj: string;
  adres: string;
  telefon: string;
  aciklama: string;
}

type KurumRow = Kurum & WithId;

const COLLECTION = "anlasmaliKurumlar";
// Mobildeki kategori ikonlarıyla uyumlu kategoriler.
const KATEGORILER = ["Restoran", "Sağlık", "Spor", "Eğitim", "Market", "Diğer"];

const bos: Kurum = { ad: "", kategori: "Restoran", avantaj: "", adres: "", telefon: "", aciklama: "" };

const AnlasmaliKurumlarPage: React.FC = () => {
  const [rows, setRows] = useState<KurumRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editId, setEditId] = useState<string | null>(null); // null=kapalı, ''=yeni, id=düzenle
  const [form, setForm] = useState<Kurum>(bos);
  const [formError, setFormError] = useState("");

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      setRows(await fetchAll<Kurum>(COLLECTION));
    } catch (e) {
      console.error("Anlaşmalı kurumlar okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(bos); setEditId(""); setFormError(""); };
  const openEdit = (r: KurumRow) => {
    setForm({ ad: r.ad, kategori: r.kategori || "Diğer", avantaj: r.avantaj, adres: r.adres, telefon: r.telefon, aciklama: r.aciklama });
    setEditId(r.id);
    setFormError("");
  };
  const close = () => { setEditId(null); setFormError(""); };

  const save = async () => {
    setFormError("");
    if (!form.ad.trim() || !form.kategori.trim() || !form.avantaj.trim()) {
      setFormError("Ad, kategori ve avantaj zorunludur.");
      return;
    }
    setSaving(true);
    try {
      const payload: Kurum = {
        ad: form.ad.trim(),
        kategori: form.kategori.trim(),
        avantaj: form.avantaj.trim(),
        adres: form.adres.trim(),
        telefon: form.telefon.trim(),
        aciklama: form.aciklama.trim(),
      };
      if (editId) await updateDocById<Kurum>(COLLECTION, editId, payload);
      else await createDoc<Kurum>(COLLECTION, payload);
      close();
      await load();
    } catch (e) {
      console.error("Kayıt başarısız:", e);
      setFormError("Kayıt başarısız, lütfen tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: KurumRow) => {
    if (!window.confirm(`"${r.ad}" silinsin mi?`)) return;
    try {
      await deleteDocById(COLLECTION, r.id);
      await load();
    } catch (e) {
      console.error("Silme başarısız:", e);
      window.alert("Silme başarısız, lütfen tekrar deneyin.");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Anlaşmalı Kurumlar</h2>
          <p className="text-sm text-gray-500 mt-0.5">{rows.length} kurum · mobil "Keşfet" ekranını besler</p>
        </div>
        <button onClick={openNew}
          className="bg-[#8B1428] hover:bg-[#6f0f20] text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors">
          <i className="fa-solid fa-plus" /> Yeni Kurum Ekle
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Kurumlar yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-store text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz anlaşmalı kurum eklenmemiş</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ad</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Kategori</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Avantaj</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Adres</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Telefon</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">{r.ad}</td>
                  <td className="px-4 py-3"><span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-50 text-blue-700">{r.kategori}</span></td>
                  <td className="px-4 py-3 text-[#8B1428] font-semibold text-xs">{r.avantaj}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">{r.adres || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs hidden sm:table-cell">{r.telefon || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => openEdit(r)} className="text-blue-600 hover:text-blue-800 text-xs font-semibold">Düzenle</button>
                      <button onClick={() => remove(r)} className="text-gray-400 hover:text-red-500"><i className="fa-solid fa-trash text-xs" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Ekle/Düzenle Modal */}
      {editId !== null && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={close} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative max-h-[90vh] overflow-y-auto">
              <button onClick={close} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><i className="fa-solid fa-xmark text-lg" /></button>
              <h3 className="text-lg font-bold text-gray-800 mb-5">{editId ? "Kurumu Düzenle" : "Yeni Kurum"}</h3>
              <div className="space-y-4">
                <Field label="Ad *"><input className={inputCls} value={form.ad} onChange={e => setForm({ ...form, ad: e.target.value })} /></Field>
                <Field label="Kategori *">
                  <select className={inputCls} value={form.kategori} onChange={e => setForm({ ...form, kategori: e.target.value })}>
                    {KATEGORILER.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </Field>
                <Field label="Avantaj *"><input className={inputCls} value={form.avantaj} onChange={e => setForm({ ...form, avantaj: e.target.value })} placeholder="%10 indirim" /></Field>
                <Field label="Adres"><input className={inputCls} value={form.adres} onChange={e => setForm({ ...form, adres: e.target.value })} /></Field>
                <Field label="Telefon"><input className={inputCls} value={form.telefon} onChange={e => setForm({ ...form, telefon: e.target.value })} /></Field>
                <Field label="Açıklama"><textarea rows={3} className={inputCls} value={form.aciklama} onChange={e => setForm({ ...form, aciklama: e.target.value })} /></Field>
                {formError && <div className="text-red-600 text-sm">{formError}</div>}
                <div className="flex gap-3 pt-2">
                  <button onClick={close} className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg hover:bg-gray-50">İptal</button>
                  <button onClick={save} disabled={saving}
                    className="flex-1 bg-[#8B1428] hover:bg-[#6f0f20] text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-60">
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30 text-gray-900";
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    {children}
  </div>
);

export default AnlasmaliKurumlarPage;
